import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Handle, type NodeProps, Position } from '@xyflow/react';
import classNames from 'classnames';

import { Badge } from '@/components/atoms/Badge';
import type { LineageNode } from '@/domain/fishGroups';
import { formatDateShortLocalized } from '@/utils/date';

import FishGroupStatusBadge from '../../FishGroupStatusBadge';

import styles from './LineageGraph.module.scss';

export interface LineageNodeData extends Record<string, unknown> {
    node: LineageNode;
    isRoot: boolean;
    onNavigate: (fishGroupId: number) => void;
}

function LineageGraphNode(props: NodeProps) {
    const { t, i18n } = useTranslation('fishGroups');
    const { node, isRoot, onNavigate } = props.data as LineageNodeData;
    const { fishGroup, depth, currentCageId, currentCageName } = node;
    const handleClick = () => {
        if (!isRoot) onNavigate(fishGroup.id);
    };

    const dateLabel = formatLifecycleRange(
        fishGroup.lifecycleStartedAt,
        fishGroup.lifecycleEndedAt,
        i18n.language,
        t
    );

    // Hide the cage line for `pending` groups (no location row yet) or when
    // the backend explicitly returned a null cage. Both cases mean the FG
    // has no meaningful "where it lives" answer.
    const showCage =
        fishGroup.status !== 'pending' && currentCageId !== null && currentCageName !== null;

    return (
        <div
            className={classNames(styles.node, styles[`node--${fishGroup.status}`], {
                [styles['node--root']]: isRoot,
            })}
            onClick={handleClick}
            role={isRoot ? 'group' : 'button'}
            tabIndex={isRoot ? -1 : 0}
            onKeyDown={(e) => {
                if (isRoot) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onNavigate(fishGroup.id);
                }
            }}>
            <Handle
                type="target"
                position={Position.Left}
                className={styles.handle}
                isConnectable={false}
            />
            <div className={styles.nodeHeader}>
                <span className={styles.nodeName} title={fishGroup.name}>
                    {fishGroup.name}
                </span>
                <Badge size="xs">d{depth}</Badge>
            </div>
            <FishGroupStatusBadge status={fishGroup.status} className={styles.status} />
            {dateLabel && <span className={styles.nodeDates}>{dateLabel}</span>}
            {showCage && (
                <span className={styles.nodeCage} title={currentCageName ?? undefined}>
                    {t('detail.lineage.node.cage', { name: currentCageName })}
                </span>
            )}
            <Handle
                type="source"
                position={Position.Right}
                className={styles.handle}
                isConnectable={false}
            />
        </div>
    );
}

function formatLifecycleRange(
    startedAt: string | null,
    endedAt: string | null,
    locale: string,
    t: (key: string, options?: Record<string, unknown>) => string
): string | null {
    if (!startedAt) return null;
    const from = formatDateShortLocalized(startedAt, locale);
    if (endedAt) {
        return t('detail.lineage.node.range', {
            from,
            to: formatDateShortLocalized(endedAt, locale),
        });
    }
    return t('detail.lineage.node.started', { date: from });
}

export default memo(LineageGraphNode);
